import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Bell, Clock, Send, Inbox as InboxIcon, Mailbox, Stamp, BookOpen, Eye, MessageSquare, XCircle, Smile, Frown, Minus, AlertCircle, ImagePlus, Mail, MailOpen, Undo2, UserCheck, PackageOpen, Warehouse, Truck, X } from 'lucide-react';
import {
  type LetterState,
  type PostageClass,
  POSTAGE_LABELS,
  redactionNotice,
  isPastLastPost,
  nextCollectionTime,
  tasterPhrase,
} from '@toiyp/shared';
import { FrankedStamp, PostageStamp, TravellingLetter } from '../postage/Stamp';
import { etaLong } from '../postage/format';
import { LetterPaper, PaperToggle, usePlainPaper } from '../stationery/LetterPaper';
import { PaperPreviewButton } from '../stationery/PaperPreview';
import { Envelope, EnvelopeOpening, EnvelopeThumb, shouldPlayOpening } from '../stationery/Envelope';
import { useMeQuery } from '../profiles/api';
import { LetterPageStamp, LetterStamp, PostageOfferModal, sealLabel, usePostageChoice, useShowsStamp } from '../postage/Postage';
import { ComposeStamp, DockedStampBook } from '../postage/StampPicker';
import { sealPause } from '../postage/bookMemory';
import { downgradeNotice, postageNotice } from '../postage/notice';
import { useAppSettings, useDeliveryConfig, useLetterReadiness, zoneLabel, type LetterReadiness } from '../settings/api';
import { usePasteGuard } from './usePasteGuard';
import { useDialogTrap } from './useDialogTrap';
import { arrivesPhrase, postStage } from './eta';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useMineQuery } from '../relationships/api';
import { fileToJpeg, type EncodedPhoto } from './photo';
import { PolaroidStack } from '../photos/Polaroid';
import { printCaption } from '../photos/scrapbook';
import { composeMsOf, type Draft } from '../drafts/storage';
import { forgetDraft, loadDrafts, persistDraft, updated } from '../drafts/draftsSlice';
import { enqueueSeal, flushPendingSeals } from '../offline/sealQueueSlice';
import { trackLetterSealed } from '../onboarding/track';
import {
  useAcceptOvertureMutation,
  useAnswerMutation,
  useAttachPhotoMutation,
  useCancelMutation,
  useDeclineOvertureMutation,
  useInboxQuery,
  useNudgeMutation,
  useOpenMutation,
  useOutboxQuery,
  useReviseMutation,
  useSealMutation,
  useUndoDeclineOvertureMutation,
  MAX_CAPTION_CHARS,
  type ApiLetter,
} from './api';
import { AcknowledgeChip, AcknowledgedNote } from '../personal/Acknowledge';
import { ComposerNotes } from '../personal/Notes';
import { LetterSignature } from '../personal/Signature';

/* ── Timestamps ──────────────────────────────────────────────────────────── */

/** Compact stamp for rows/bubbles: "14:07" today, "2 Sep 14:07" otherwise. */
export function formatStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

/** Full stamp for the letter detail: "2 September 2026 at 14:07". */
export function formatFullStamp(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const date = d.toLocaleDateString([], {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${date} at ${time}`;
}

/**
 * The timestamp a bubble shows for a letter: when the sender sealed it (mine),
 * or when it arrived for the recipient (theirs), falling back to send time
 * while it is still travelling.
 */
/** The excerpt a letter on its way shows — the same 140 characters as the reader's peek. */
export function letterExcerpt(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > 140 ? `${flat.slice(0, 140)}…` : flat;
}

export function bubbleStamp(
  letter: ApiLetter,
  mine: boolean,
): { label: 'sent' | 'arrived'; text: string } | null {
  if (mine) return letter.sealedAt ? { label: 'sent', text: formatStamp(letter.sealedAt) } : null;
  if (letter.deliveredAt) return { label: 'arrived', text: formatStamp(letter.deliveredAt) };
  return letter.sealedAt ? { label: 'sent', text: formatStamp(letter.sealedAt) } : null;
}

/** Server-authoritative countdown */
export function Countdown({ deliverAt }: { deliverAt: string | null }) {
  const target = deliverAt ? new Date(deliverAt).getTime() : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (target == null) return <span className="pill">not sealed yet</span>;

  const remaining = target - now;
  if (remaining <= 0) return <span className="pill pill--ready">ready</span>;

  const mins = Math.round(remaining / 60000);
  const label = mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`;
  return <span className="pill"><Clock size={10} aria-hidden="true" /> {label}</span>;
}

/**
 * The arrival reveal (F10): "arriving Thursday" instead of a bare ticking
 * clock — mail, not a chat timer. The exact time stays one hover away
 * (the `title`); nothing here pretends the window is wider than it is.
 */
export function arrivalDay(deliverAt: string | null): string {
  if (!deliverAt) return '';
  const d = new Date(deliverAt);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return 'today';
  if (d.toDateString() === tomorrow.toDateString()) return 'tomorrow';
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
    new Date(deliverAt).getDay()
  ]!;
}

/**
 * "In the post": the envelope travelling from postmark to letterbox.
 *
 * Visual progress spans sealedAt → deliverAt (the whole journey the sender
 * watches). The stage label transitions at collectedAt and then through the
 * transit window, so the words match what is actually happening to the letter.
 */
export function InThePost({
  state,
  sealedAt,
  collectedAt,
  deliverAt,
  postageClass,
}: {
  state: LetterState;
  sealedAt: string | null;
  collectedAt: string | null;
  deliverAt: string | null;
  /** The stamp the letter carries — it rides on the travelling letter when postage is on. */
  postageClass?: PostageClass;
}) {
  const showClass = useShowsStamp(postageClass);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!deliverAt) return null;

  const start = sealedAt ? new Date(sealedAt).getTime() : now;
  const end = new Date(deliverAt).getTime();
  const collected = collectedAt ? new Date(collectedAt).getTime() : null;

  // Position on the road is time through the journey (seal → letterbox), never
  // the state: `in_transit` spans the whole trip since collections.
  const progress = Math.min(1, Math.max(0, (now - start) / Math.max(1, end - start)));
  const stage = postStage({ now, collectedAt: collected, deliverAt: end, state, postageClass });
  const arrives = arrivesPhrase(end, new Date(now));
  const pct = `${Math.round(progress * 100)}%`;

  return (
    <span
      className="in-the-post"
      role="img"
      aria-label={`${stage}. ${showClass ? `${POSTAGE_LABELS[postageClass!]}, ` : ''}arrives ${arrives}.`}
    >
      <span className="in-the-post__route" aria-hidden="true">
        <span className="in-the-post__end" title={sealedAt ? `Posted ${formatFullStamp(sealedAt)}` : undefined}>
          <Stamp size={12} />
        </span>
        <span className="in-the-post__track">
          <span className="in-the-post__fill" style={{ width: pct }} />
          {showClass ? (
            <span className="in-the-post__letter" style={{ left: pct }} title={`${POSTAGE_LABELS[postageClass!]} letter`}>
              <TravellingLetter cls={postageClass!} />
            </span>
          ) : (
            <span className="in-the-post__envelope" style={{ left: pct }}>
              <Mail size={13} />
            </span>
          )}
        </span>
        <span className="in-the-post__end in-the-post__end--box" title={`Arrives ${formatFullStamp(deliverAt)}`}>
          <Mailbox size={12} />
        </span>
      </span>
      <span className="in-the-post__labels" aria-hidden="true">
        <span className="in-the-post__stage">{stage}</span>
        <span className="in-the-post__eta" title={formatFullStamp(deliverAt)}>
          arrives {arrives}
        </span>
      </span>
    </span>
  );
}

/**
 * A small advisory shown in compose and outbox views when the last collection
 * of the working day has passed. No letter sealed now will move until tomorrow.
 */
export function LastPostHint() {
  const cfg = useDeliveryConfig();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!isPastLastPost(now, cfg)) return null;
  const next = nextCollectionTime(now, cfg);
  const nextDay = next.toLocaleDateString([], { weekday: 'long' });
  const nextTime = next.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <span className="pill pill--last-post" title={`Next collection: ${nextDay} at ${nextTime}`}>
      <Clock size={10} aria-hidden="true" /> Last post has gone · collected {nextDay}
    </span>
  );
}

const STATE_META: Record<LetterState, { label: string; cls: string; Icon: React.ElementType }> = {
  draft:      { label: 'draft',      cls: '',               Icon: Minus         },
  sealed:     { label: 'in the post', cls: 'pill--sealed',  Icon: Clock         },
  in_transit: { label: 'on the round', cls: 'pill--transit', Icon: Truck        },
  delivered:  { label: 'arrived',    cls: 'pill--delivered', Icon: InboxIcon    },
  opened:     { label: 'read',       cls: 'pill--opened',   Icon: BookOpen      },
  answered:   { label: 'answered',   cls: 'pill--answered', Icon: MessageSquare },
  cancelled:  { label: 'stopped',    cls: 'pill--cancelled', Icon: XCircle      },
};

export function StatePill({ state }: { state: LetterState }) {
  const meta = STATE_META[state] ?? { label: state.replace('_', ' '), cls: '', Icon: null };
  return (
    <span className={`pill ${meta.cls}`}>
      {meta.Icon && <meta.Icon size={10} aria-hidden="true" />}
      {meta.label}
    </span>
  );
}

const SENTIMENT_META: Record<string, { cls: string; Icon: React.ElementType }> = {
  warm:    { cls: 'pill--warm',    Icon: Smile },
  tense:   { cls: 'pill--tense',  Icon: Frown },
  neutral: { cls: 'pill--neutral', Icon: Minus },
};

export function SentimentPill({ sentiment }: { sentiment: string | null }) {
  if (!sentiment) return null;
  const meta = SENTIMENT_META[sentiment] ?? { cls: 'pill--neutral', Icon: Minus };
  return (
    <span className={`pill ${meta.cls}`}>
      <meta.Icon size={10} aria-hidden="true" />
      {sentiment}
    </span>
  );
}

/** "a 4-hour journey" — how long the letter spent in the post. */
function journeyLength(sealedAt: string | null, deliveredAt: string | null): string {
  if (!sealedAt || !deliveredAt) return 'travelled';
  const mins = Math.max(1, Math.round((new Date(deliveredAt).getTime() - new Date(sealedAt).getTime()) / 60000));
  if (mins < 60) return `a ${mins}-minute journey`;
  const hours = Math.round(mins / 60);
  return `a ${hours}-hour journey`;
}

type PostStep = {
  key: string;
  title: string;
  Icon: React.ElementType;
  /** When it happened, or what is expected, in the post office's words. */
  note: string;
  status: 'done' | 'active' | 'pending' | 'stopped';
};

/**
 * The letter's postal record: a vertical tracking log from the wax seal to
 * the reply, each step stamped with when it happened. While the letter is
 * still travelling, the "in the post" step carries the live route tracker and
 * the granular stage name (In the postbox → Collected → Sorting office → Out
 * for delivery).
 */
export function Journey({ letter, state }: { letter: ApiLetter; state: LetterState }) {
  const at = (iso: string | null) => (iso ? formatFullStamp(iso) : '');
  const travelling = state === 'sealed' || state === 'in_transit';
  const order: LetterState[] = ['sealed', 'in_transit', 'delivered', 'opened', 'answered'];
  const reached = order.indexOf(state);
  const status = (i: number): PostStep['status'] =>
    i < reached ? 'done' : i === reached ? 'active' : 'pending';

  const { postageEnabled } = useAppSettings();
  const cls = letter.postageClass ?? 'second';
  const design = letter.stampDesign ?? null;
  const showClass = postageEnabled || cls !== 'second' || design != null;
  const express = cls === 'express';

  // The collection note: when was/is the letter picked up?
  const collectionNote = (): string => {
    if (express) return 'Express — straight from the seal, no postbox wait';
    if (letter.collectedAt) {
      const d = new Date(letter.collectedAt);
      return d.getTime() <= Date.now()
        ? `collected ${at(letter.collectedAt)}`
        : `collection due ${arrivalDay(letter.collectedAt)}, ${at(letter.collectedAt)}`;
    }
    return 'awaiting collection';
  };

  const steps: PostStep[] =
    state === 'cancelled'
      ? [
          { key: 'sealed', title: 'Sealed & posted', Icon: Stamp, note: at(letter.sealedAt), status: 'done' },
          {
            key: 'stopped',
            title: 'Taken back from the postbox',
            Icon: XCircle,
            note: 'Stopped before it flew — it was never delivered.',
            status: 'stopped',
          },
        ]
      : [
          {
            key: 'sealed',
            title: showClass ? `Sealed & posted ${POSTAGE_LABELS[cls]}` : 'Sealed & posted',
            Icon: Stamp,
            note: at(letter.sealedAt),
            status: reached >= 0 ? 'done' : 'pending',
          },
          {
            key: 'collected',
            title: 'Collected from postbox',
            Icon: PackageOpen,
            note: collectionNote(),
            // Collected once in_transit or beyond; active while still sealed.
            status: reached >= 1 ? 'done' : reached === 0 ? 'active' : 'pending',
          },
          {
            key: 'in_post',
            title: 'In the post',
            Icon: Mail,
            note: travelling
              ? letter.deliverAt
                ? `due ${arrivalDay(letter.deliverAt)}, ${at(letter.deliverAt)}`
                : 'on its way'
              : journeyLength(letter.sealedAt, letter.deliveredAt),
            status: travelling ? 'active' : reached > 1 ? 'done' : 'pending',
          },
          {
            key: 'delivered',
            title: 'Through the letterbox',
            Icon: Mailbox,
            note: letter.deliveredAt ? at(letter.deliveredAt) : 'waiting on the round',
            status: status(2),
          },
          {
            key: 'opened',
            title: 'Opened & read',
            Icon: MailOpen,
            note: letter.openedAt ? at(letter.openedAt) : 'not opened yet',
            status: status(3),
          },
          {
            key: 'answered',
            title: 'Answered',
            Icon: MessageSquare,
            note: letter.answeredAt ? at(letter.answeredAt) : 'no reply yet',
            status: status(4),
          },
        ];

  return (
    <section className="postal-log" aria-label="Postal record">
      <header className="postal-log__head">
        <span className="postal-log__title">Postal record</span>
        {showClass && (
          <span
            className={`postal-log__stamp${design ? ' postal-log__stamp--design' : ''}`}
            title={`Sent ${POSTAGE_LABELS[cls]}${design ? ` · ${design.name}` : ''}`}
          >
            <FrankedStamp
              cls={cls}
              design={design}
              size={design ? 'medium' : 'small'}
              date={letter.collectedAt && new Date(letter.collectedAt).getTime() <= Date.now() ? new Date(letter.collectedAt) : null}
            />
          </span>
        )}
        {letter.envelope && <EnvelopeThumb envelope={letter.envelope} />}
        {letter.sealedAt && (
          <span className="postal-log__postmark" aria-hidden="true">
            <span>toiyp</span>
            <span>{new Date(letter.sealedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })}</span>
          </span>
        )}
      </header>
      <ol className="postal-log__steps">
        {steps.map(({ key, title, Icon, note, status: st }) => (
          <li key={key} className={`postal-log__step postal-log__step--${st}`}>
            <span className="postal-log__mark" aria-hidden="true">
              <Icon size={14} />
            </span>
            <span className="postal-log__body">
              <span className="postal-log__step-title">
                {title}
                {st === 'active' && <span className="visually-hidden"> (current)</span>}
              </span>
              {note && <span className="postal-log__note">{note}</span>}
              {key === 'in_post' && travelling && (
                <InThePost
                  state={state}
                  sealedAt={letter.sealedAt}
                  collectedAt={letter.collectedAt}
                  deliverAt={letter.deliverAt}
                  postageClass={cls}
                />
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Explains the placeholders in a letter body. Without this the reader sees a
 * mangled sentence and no reason for it — which reads as a bug, or worse, as
 * the sender being evasive.
 */
export function RedactionNote({ kinds }: { kinds: NonNullable<ApiLetter['redactedKinds']> }) {
  const notice = redactionNotice(kinds);
  if (!notice) return null;
  return (
    <div className="quality-card quality-card--redaction">
      <p className="quality-why quality-why--plain">{notice}</p>
    </div>
  );
}

export function QualityNote({ quality }: { quality: ApiLetter['quality'] }) {
  if (!quality) return null;

  if (quality.composite == null) {
    // No algorithm version means the letter has not been read yet (the reading
    // is still on its way, or being retried) — not that it could not be.
    if (quality.degraded && quality.algorithmVersion) {
      return (
        <div className="quality-card">
          <p className="quality-why quality-why--plain">We weren't able to read this one this time, so there are no notes on it.</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="quality-card">
      <div className="quality-card__row">
        <span className="pill pill--score" title={`v${quality.algorithmVersion ?? ''}`}>
          {Math.round(quality.composite * 100)}/100
        </span>
        <SentimentPill sentiment={quality.sentiment} />
      </div>
      {quality.why.map((m) => (
        <p key={m} className="quality-why">{m}</p>
      ))}
    </div>
  );
}

export function ReadReceipt({ receipt }: { receipt: ApiLetter['readReceipt'] }) {
  if (!receipt) return null;
  const times = receipt.reads
    .map((t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    .join(', ');
  return (
    <p className="read-receipt">
      Postscript — your last letter was read {receipt.readCount}{' '}
      {receipt.readCount === 1 ? 'time' : 'times'}: {times}.
    </p>
  );
}

/** Full letter view used in letter detail screen */
/* ── Overture (§11c) ─────────────────────────────────────────────────── */

/**
 * The badge that says "this is first contact, not correspondence". It is the
 * one cue that must survive everywhere the letter appears, because every other
 * affordance on the row assumes two people who chose each other.
 */
export function OverturePill({ decision }: { decision: ApiLetter['overtureDecision'] }) {
  const tone =
    decision === 'accepted'
      ? 'pill--overture-accepted'
      : decision === 'declined'
        ? 'pill--overture-declined'
        : 'pill--overture-pending';
  const label =
    decision === 'accepted' ? 'Overture · accepted' : decision === 'declined' ? 'Overture · declined' : 'Overture';
  return <span className={`pill ${tone}`}>{label}</span>;
}

/** How long an accept-note may be. Mirrors the server's cap; no floor, by design. */
const OVERTURE_NOTE_LIMIT = 600;

/**
 * The decision surface for an overture. An overture has no Reply button — the
 * answer to first contact is whether it is wanted at all, and that has to be a
 * choice rather than a keystroke. Accepting emails an account link; declining
 * sends a refusal and stops their mail arriving.
 */
export function OvertureCard({ letter, mine }: { letter: ApiLetter; mine: boolean }) {
  const [accept, { isLoading: accepting }] = useAcceptOvertureMutation();
  const [decline, { isLoading: declining }] = useDeclineOvertureMutation();
  const [undo, { isLoading: undoing }] = useUndoDeclineOvertureMutation();
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const decision = letter.overtureDecision;
  const address = letter.overture?.fromEmail ?? 'an address that has no account here';
  const busy = accepting || declining || undoing;

  const onAccept = useCallback(async () => {
    setError(null);
    try {
      await accept({ id: letter.id, note: note.trim() || null }).unwrap();
      setNote('');
      setNoteOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send the invite');
    }
  }, [accept, letter.id, note]);

  const onDecline = useCallback(async () => {
    setError(null);
    try {
      await decline({ id: letter.id }).unwrap();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not decline');
    }
  }, [decline, letter.id]);

  const onUndo = useCallback(async () => {
    setError(null);
    try {
      await undo({ id: letter.id }).unwrap();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not undo the decline');
    }
  }, [undo, letter.id]);

  // The sender's side: they wrote it, so there is nothing to decide. Say what
  // happened to it so the state is never a mystery.
  if (mine) {
    return (
      <div className="overture-card">
        <div className="overture-card__head">
          <OverturePill decision={decision} />
        </div>
        <p className="overture-card__line">
          {decision === 'accepted'
            ? `They accepted your letter — an invite is on its way to ${address}.`
            : decision === 'declined'
              ? 'This overture was declined. Nothing you send will reach them.'
              : `Waiting for ${address} to decide whether they want to hear from you.`}
        </p>
      </div>
    );
  }

  if (decision === 'accepted') {
    return (
      <div className="overture-card overture-card--accepted">
        <div className="overture-card__head">
          <OverturePill decision={decision} />
          <UserCheck size={14} aria-hidden="true" />
        </div>
        <p className="overture-card__line">
          You accepted their letter, and an invite is on its way to{' '}
          <strong>{address}</strong>
          {letter.decidedAt ? ` (sent ${formatStamp(letter.decidedAt)})` : ''}. When they join and write to you,
          that is when you can write back.
        </p>
      </div>
    );
  }

  if (decision === 'declined') {
    return (
      <div className="overture-card overture-card--declined">
        <div className="overture-card__head">
          <OverturePill decision={decision} />
          <XCircle size={14} aria-hidden="true" />
        </div>
        <p className="overture-card__line">
          You declined this overture. A refusal has been sent to <strong>{address}</strong>, and
          anything they write will not reach you.
        </p>
        {error && (
          <p className="form-error" role="alert">
            <AlertCircle size={13} aria-hidden="true" />{error}
          </p>
        )}
        <div className="overture-card__actions">
          <button type="button" className="btn btn--ghost" disabled={undoing} onClick={() => void onUndo()}>
            <Undo2 size={14} aria-hidden="true" />
            {undoing ? 'Undoing…' : 'Undo decline'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overture-card">
      <div className="overture-card__head">
        <OverturePill decision={decision} />
        <MailOpen size={14} aria-hidden="true" />
      </div>
      <p className="overture-card__line">
        <strong>{address}</strong> wrote to your address. This is their first contact — they have no
        account here. Accept to invite them, or decline and their mail will not reach you.
      </p>

      {noteOpen && (
        <form
          className="reply-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onAccept();
          }}
        >
          <label htmlFor={`overture-note-${letter.id}`} className="visually-hidden">
            A note to include with the invite (optional)
          </label>
          <textarea
            id={`overture-note-${letter.id}`}
            className="form-textarea"
            value={note}
            maxLength={OVERTURE_NOTE_LIMIT}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional: a line they'll read first, before the invite link."
            rows={4}
          />
          <p className="overture-card__hint">
            {note.trim().length === 0
              ? 'No note needed — they get the invite on its own.'
              : `${note.trim().length}/${OVERTURE_NOTE_LIMIT} — this opens their letter to you.`}
          </p>
        </form>
      )}

      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" />{error}
        </p>
      )}

      <div className="overture-card__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy}
          onClick={() => (noteOpen ? void onAccept() : setNoteOpen(true))}
        >
          <Mail size={14} aria-hidden="true" />
          {accepting ? 'Sending…' : noteOpen ? 'Send invite' : 'Accept & invite'}
        </button>
        {noteOpen && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={() => {
              setNoteOpen(false);
              setNote('');
            }}
          >
            Cancel
          </button>
        )}
        <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void onDecline()}>
          <XCircle size={14} aria-hidden="true" />
          {declining ? 'Declining…' : 'Decline'}
        </button>
      </div>
    </div>
  );
}

/* ── Shared compose affordances ────────────────────────────────────────────── */

/** The API's own words when it refuses a letter, so the writer knows why. */
export function apiErrorMessage(e: unknown, fallback: string): string {
  const m = (e as { data?: { message?: string | string[] } } | undefined)?.data?.message;
  return Array.isArray(m) ? m[0] ?? fallback : m ?? fallback;
}

/**
 * A human "when was this saved" label that re-renders on a slow tick. The
 * draft's `updatedAt` is a number, so this is the only place a time is needed
 * as a number rather than an ISO string.
 */
export function SavedAgo({ at, prefix = 'saved' }: { at?: number; prefix?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  if (!at) return <span className="compose-saved compose-saved--empty">saved locally</span>;
  const secs = Math.max(0, Math.round((now - at) / 1000));
  const label =
    secs < 45 ? 'just now' : secs < 3600 ? `${Math.round(secs / 60)}m ago` : `${Math.round(secs / 3600)}h ago`;
  return (
    <span className="compose-saved" title={new Date(at).toLocaleString()}>
      {prefix} {label}
    </span>
  );
}

/**
 * First-seal education modal: explains the three daily collection rounds.
 * Shown once (gated by localStorage key `toiyp:postage_intro`), then dismissed.
 * With postage on, it also says which class the letter went and when it lands.
 */
export function PostageModal({
  onDismiss,
  sent,
}: {
  onDismiss: () => void;
  sent?: { postageClass: PostageClass; deliverAt: string | null } | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogTrap(ref, onDismiss);
  const { deliveryStartTime, deliveryEndTime, postTimezone } = useAppSettings();

  return (
    <div className="postage-overlay" onClick={onDismiss} aria-hidden="false">
      <div
        className="postage-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Collection times"
        ref={ref}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="postage-modal__icon" aria-hidden="true">
          <Stamp size={22} />
        </div>
        <h2 className="postage-modal__title">Your letter is in the post</h2>
        <p className="postage-modal__sub">
          The post van collects from the postbox three times a day, Monday to Saturday ({zoneLabel(postTimezone)}).
        </p>
        <ul className="postage-modal__rounds" aria-label="Daily collection rounds">
          <li>
            <span className="postage-modal__time">09:00</span>
            <span className="postage-modal__round-name">Morning post</span>
          </li>
          <li>
            <span className="postage-modal__time">13:00</span>
            <span className="postage-modal__round-name">Midday post</span>
          </li>
          <li>
            <span className="postage-modal__time">17:00</span>
            <span className="postage-modal__round-name">Last post</span>
          </li>
        </ul>
        <p className="postage-modal__note">
          Miss the last post and your letter waits in the postbox overnight, collected with the morning round.
          Letters come through the door between {deliveryStartTime} and {deliveryEndTime} {zoneLabel(postTimezone)} — never in
          the night.
        </p>
        {sent?.deliverAt && (
          <p className="postage-modal__sent">
            <PostageStamp cls={sent.postageClass} size="mini" />
            Sent {POSTAGE_LABELS[sent.postageClass]}, {etaLong(sent.deliverAt).replace(/^arrives/, 'arriving')}.
          </p>
        )}
        <button
          type="button"
          className="btn btn--primary postage-modal__cta"
          onClick={onDismiss}
          style={{ borderRadius: 'var(--r-full)' }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/** The 200-char floor as a bar + counter, shared by every write surface. */
export function ComposeProgress({ progress, notice }: { progress: LetterReadiness; notice?: string | null }) {
  // A paste turned back speaks first; otherwise the quality floor, once the
  // length is met (useLetterReadiness only judges quality from there).
  const hint = notice ?? progress.quality.message ?? null;
  return (
    <>
      <div
        className="letter-progress"
        data-ready={progress.sendable ? 'true' : 'false'}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.required}
        aria-valuenow={Math.min(progress.length, progress.required)}
        aria-label={`Letter length: ${progress.length} of ${progress.required} characters needed`}
      >
        <div className="letter-progress__track">
          <div className="letter-progress__fill" style={{ width: `${Math.round(progress.ratio * 100)}%` }} />
        </div>
        <span className="letter-progress__label">
          {progress.sendable
            ? `${progress.length} characters · ready to seal`
            : progress.ready
              ? `${progress.length} characters · not quite yet`
              : `${progress.length} / ${progress.required} characters · ${progress.remaining} more to seal`}
        </span>
      </div>
      {hint && (
        <p className="letter-progress__hint" role="status">
          {hint}
        </p>
      )}
    </>
  );
}

/** A letter you can answer gets the stamp book docked beside its postal record. */
function dockableFor(mode: 'inbox' | 'outbox', letter: ApiLetter, replyEnabledProp: boolean | undefined): boolean {
  const noReply = letter.kind === 'overture' || letter.kind === 'system-letter';
  const answerable = mode === 'inbox' && !noReply && (letter.state === 'delivered' || letter.state === 'opened');
  return answerable && replyEnabledProp !== false;
}

export function LetterRow({
  letter,
  mode,
  replyVisible: replyVisibleProp,
  replyEnabled: replyEnabledProp,
  canNudge = false,
  openNudge = false,
}: {
  letter: ApiLetter;
  mode: 'inbox' | 'outbox';
  /** Override whether the reply button is shown at all */
  replyVisible?: boolean;
  /** Override whether the reply button is enabled (false = greyed out) */
  replyEnabled?: boolean;
  /** Show the nudge panel for this outbox letter */
  canNudge?: boolean;
  /** Auto-open the nudge form when the screen mounts */
  openNudge?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [openLetter] = useOpenMutation();
  const [answerLetter, { isLoading: answering }] = useAnswerMutation();
  const [cancelLetter, { isLoading: cancelling }] = useCancelMutation();
  const [reviseLetter, { isLoading: revising }] = useReviseMutation();
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(letter.body);
  const [error, setError] = useState<string | null>(null);

  // A reply is a letter to the same person, so it drafts under the same key as
  // the compose box (they cannot be open at once — compose is for empty/settled
  // threads, reply for an arrived letter). Sharing the key means a half-written
  // reply survives a closed tab exactly like a half-written letter does.
  const draft = useAppSelector((s) => s.drafts.byId[letter.relationshipId]);
  const replyBody = draft?.body ?? '';
  const replyProgress = useLetterReadiness(replyBody);
  const replyPaste = usePasteGuard();
  const postage = usePostageChoice(letter.relationshipId, { skip: !(replying || dockableFor(mode, letter, replyEnabledProp)) });
  const [sealing, setSealing] = useState(false);

  const from = letter.peer?.handle ?? letter.peer?.displayName ?? 'someone';
  const state = letter.state as LetterState;
  const peek = mode === 'inbox' && letter.peek;
  const taster = tasterPhrase(letter.taster);
  const cancelled = state === 'cancelled';
  const canOpen = mode === 'inbox' && state === 'delivered';
  const isOverture = letter.kind === 'overture';
  // An overture is answered by accepting or declining it, never by replying —
  // so the Reply affordance must not exist on one, only the decision surface.
  // The Post Office's own letters take no reply at all (the API refuses one).
  const noReply = isOverture || letter.kind === 'system-letter';
  const canReplyInternal =
    mode === 'inbox' && !noReply && (state === 'delivered' || state === 'opened');
  const showReply = (replyVisibleProp ?? canReplyInternal) && !noReply;
  const replyEnabled = (replyEnabledProp ?? canReplyInternal) && !noReply;
  const canCooldown = mode === 'outbox' && state === 'sealed';
  // The stamp book docks beside the postal record of a letter you can answer;
  // a list of rows that can't be answered should not each ask for postage.
  const dockable = dockableFor(mode, letter, replyEnabledProp);
  const dockRef = useRef<HTMLElement>(null);
  const [dockHighlight, setDockHighlight] = useState(false);
  const showDock = dockable && (postage.enabled || postage.hasDesigns);
  const pointAtDock = useCallback(() => {
    dockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setDockHighlight(true);
    window.setTimeout(() => setDockHighlight(false), 1400);
  }, []);

  // A half-written reply reopens where it was left, rather than hiding behind
  // the Reply button. Once only, so Cancel still closes it.
  const resumed = useRef(false);
  useEffect(() => {
    if (resumed.current || !replyEnabled || !showReply || !replyBody.trim()) return;
    resumed.current = true;
    setReplying(true);
  }, [replyEnabled, showReply, replyBody]);

  const sentStamp = formatFullStamp(letter.sealedAt);
  const arrivedStamp = formatFullStamp(letter.deliveredAt);
  // Stationery: the words on their paper, unless the reader prefers plain.
  const [plainPaper] = usePlainPaper();
  const paper = letter.stationery ?? null;
  // Until it is delivered a letter stays sealed for both sides: the writer
  // sees the same excerpt the reader's peek shows (the whole letter only in
  // the Revise editor), in its envelope when it has one.
  const sealedOwn = mode === 'outbox' && (state === 'sealed' || state === 'in_transit');
  const onPaper = Boolean(paper) && !plainPaper && !peek && !cancelled && !editing && !sealedOwn;
  const stampOnly = !cancelled && letter.sealedAt ? <LetterPageStamp key={letter.id} letter={letter} /> : null;
  // The envelope it came in sits beside the stamp, small.
  const pageStamp =
    stampOnly && letter.envelope ? (
      <span className="letter-page-stamp-group">
        <EnvelopeThumb envelope={letter.envelope} />
        {stampOnly}
      </span>
    ) : (
      stampOnly
    );
  // The first time a delivered letter in an envelope is opened: flap up, paper out.
  const { data: me } = useMeQuery();
  const [opening, setOpening] = useState(
    () => mode === 'inbox' && state === 'delivered' && Boolean(letter.envelope) && shouldPlayOpening(),
  );
  const finishOpening = useCallback(() => setOpening(false), []);
  // The sender's view of the letter: how many times the recipient opened it.
  const reads = mode === 'outbox' ? letter.reads.length : 0;
  // And how often they came back while it was still in the post. Never shown
  // to the reader whose visits these are — `peekCount` is null on their side.
  const peeks = mode === 'outbox' ? letter.peekCount ?? 0 : 0;
  const redactedKinds = letter.redactedKinds ?? null;

  const onOpen = useCallback(async () => {
    try { await openLetter({ id: letter.id }).unwrap(); } catch { /* idempotent */ }
  }, [openLetter, letter.id]);

  const onSubmitReply = useCallback(async () => {
    const body = replyBody.trim();
    if (!replyProgress.sendable || !body) return;
    setError(null);
    const asked = postage.selected;
    try {
      // The seal moment: the postmark lands while the reply goes off.
      const stamped = postage.enabled || postage.design != null;
      setSealing(stamped);
      const [sent] = await Promise.all([
        answerLetter({
          id: letter.id,
          body,
          postage: asked,
          composeMs: composeMsOf(draft),
          stampDesignId: postage.design?.id,
          stationeryId: postage.paper?.id,
          envelopeId: postage.envelope?.id,
        }).unwrap(),
        stamped ? sealPause() : Promise.resolve(),
      ]);
      setSealing(false);
      trackLetterSealed();
      void dispatch(forgetDraft(letter.relationshipId));
      setReplying(false);
      if (sent.postageDowngraded) postageNotice.show(downgradeNotice(asked, sent.postageClass));
    } catch (e) {
      setSealing(false);
      setError(apiErrorMessage(e, 'Could not send reply'));
    }
  }, [answerLetter, letter.id, replyBody, draft, replyProgress.sendable, dispatch, letter.relationshipId, postage.selected, postage.enabled, postage.design, postage.paper, postage.envelope]);

  const onReplyChange = useCallback(
    (next: string) => {
      const d: Draft = {
        id: letter.relationshipId,
        relationshipId: letter.relationshipId,
        body: next,
        updatedAt: Date.now(),
        // Kept from the draft already in the store, so a reply typed across
        // two sittings still counts from the first one (F5).
        startedAt: draft?.startedAt ?? Date.now(),
      };
      dispatch(updated(d));
      void dispatch(persistDraft(d));
    },
    [dispatch, letter.relationshipId, draft?.startedAt],
  );

  const onStop = useCallback(async () => {
    setError(null);
    try { await cancelLetter({ id: letter.id }).unwrap(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not stop the letter'); }
  }, [cancelLetter, letter.id]);

  const onSaveEdit = useCallback(async () => {
    const body = editBody.trim();
    if (!body) return;
    setError(null);
    try {
      await reviseLetter({ id: letter.id, body }).unwrap();
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revise');
    }
  }, [reviseLetter, letter.id, editBody]);

  if (opening && letter.envelope) {
    return (
      <article className="letter-page">
        <EnvelopeOpening
          envelope={letter.envelope}
          to={me?.handle ? `To @${me.handle}` : 'To you'}
          from={`from @${from}`}
          stamp={<LetterStamp letter={letter} />}
          onDone={finishOpening}
        />
      </article>
    );
  }

  return (
    <article className="letter-page">
      {/* Every letter wears its stamp in the corner, like the envelope it came in. */}
      {/* A sealed envelope already carries the stamp; the corner one would be a second. */}
      {!onPaper && !((sealedOwn || peek) && letter.envelope && !cancelled) && pageStamp}
      <div className="letter-meta">
        <span className="letter-from">
          {mode === 'inbox' ? 'from' : 'to'} @{from}
        </span>
        {isOverture ? <OverturePill decision={letter.overtureDecision} /> : <StatePill state={state} />}
        {peek && <SentimentPill sentiment={letter.quality?.sentiment ?? null} />}
        {/* Once it has arrived the postal record below tells the time; a "ready" chip adds nothing. */}
        {(state === 'sealed' || state === 'in_transit') && <Countdown deliverAt={letter.deliverAt} />}
        {state === 'sealed' && mode === 'outbox' && <LastPostHint />}
        <span className="letter-time">
          {mode === 'inbox'
            ? arrivedStamp
              ? `arrived ${arrivedStamp}`
              : sentStamp
                ? `sent ${sentStamp}`
                : ''
            : sentStamp
              ? `sent ${sentStamp}`
              : ''}
        </span>
        {reads > 0 && (
          <span className="pill pill--reads" title="Times the recipient opened this letter">
            read {reads} {reads === 1 ? 'time' : 'times'}
          </span>
        )}
        {peeks > 0 && (
          <span
            className="pill pill--peeks"
            title="Times they came back to this letter before it arrived"
          >
            <Eye size={9} aria-hidden="true" /> seen {peeks}×
          </span>
        )}
      </div>

      {cancelled ? (
        <p className="letter-body letter-body--sealed">
          Stopped before it flew. It was never delivered.
        </p>
      ) : peek ? (
        <>
          {letter.envelope && (
            <div className="letter-sealed-envelope">
              <Envelope
                envelope={letter.envelope}
                to={me?.handle ? `To @${me.handle}` : 'To you'}
                from={`from @${from}`}
                stamp={<LetterStamp letter={letter} />}
              />
            </div>
          )}
          <p className="letter-body letter-body--sealed">{letter.body}</p>
          {taster && <p className="letter-taster">{taster}</p>}
          {redactedKinds && <RedactionNote kinds={redactedKinds} />}
          <Journey letter={letter} state={state} />
        </>
      ) : (
        <>
          {editing ? (
            <form
              className="reply-form"
              onSubmit={(e) => { e.preventDefault(); void onSaveEdit(); }}
            >
              <label htmlFor={`revise-${letter.id}`} className="visually-hidden">Revised letter</label>
              <textarea
                id={`revise-${letter.id}`}
                className="form-textarea"
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={8}
              />
              {error && (
                <p className="form-error" role="alert">
                  <AlertCircle size={13} aria-hidden="true" />{error}
                </p>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button type="submit" className="btn btn--primary" disabled={revising || editBody.trim().length === 0}>
                  {revising ? 'Saving…' : 'Save revision'}
                </button>
              </div>
            </form>
          ) : (
            <>
              {isOverture && letter.subject && <p className="letter-subject">{letter.subject}</p>}
              {paper && !sealedOwn && <PaperToggle paper={paper} />}
              {sealedOwn ? (
                <>
                  {letter.envelope && (
                    <div className="letter-sealed-envelope">
                      <Envelope
                        envelope={letter.envelope}
                        to={`To @${from}`}
                        from={me?.handle ? `from @${me.handle}` : 'from you'}
                        stamp={<LetterStamp letter={letter} />}
                      />
                    </div>
                  )}
                  <p className="letter-body letter-body--sealed">{letterExcerpt(letter.body)}</p>
                  <p className="letter-taster">
                    Sealed{letter.envelope ? ' in its envelope' : ''}
                    {paper ? ` on ${paper.name}` : ''} — the whole letter opens when it arrives.
                  </p>
                </>
              ) : onPaper && paper ? (
                <LetterPaper paper={paper} text={letter.body} stamp={pageStamp} />
              ) : (
                <p className={`letter-body${letter.kind === 'overture' && letter.overtureDecision === 'declined' ? ' letter-body--declined' : ''}`}>{letter.body}</p>
              )}
              <LetterSignature
                path={letter.signature}
                label={mode === 'outbox' ? 'Your signature' : `Signed by @${from}`}
              />
              {letter.cta && (
                <div className="letter-cta">
                  <a className="btn btn--primary" href={letter.cta.route}>
                    {letter.cta.label}
                  </a>
                </div>
              )}
              <PolaroidStack
                photos={letter.media}
                mine={mode === 'outbox'}
                captionable={canCooldown}
                peerName={from}
                caption={printCaption({ mine: mode === 'outbox', at: letter.deliveredAt ?? letter.sealedAt }, from)}
              />
              {/* The notes are written to the author about their own letter. */}
              {mode === 'outbox' && <QualityNote quality={letter.quality} />}
              {redactedKinds && <RedactionNote kinds={redactedKinds} />}
              {mode === 'inbox' && letter.readReceipt && <ReadReceipt receipt={letter.readReceipt} />}
              {showDock ? (
                // The postal record and the stamp book for the reply, side by side.
                <div className="postal-pair">
                  <div className="postal-pair__grid">
                    <Journey letter={letter} state={state} />
                    <DockedStampBook
                      ref={dockRef}
                      choice={postage}
                      peerName={from}
                      highlight={dockHighlight}
                      onPicked={() => setReplying(true)}
                    />
                  </div>
                </div>
              ) : (
                <Journey letter={letter} state={state} />
              )}
              {mode === 'outbox' && <AcknowledgedNote letter={letter} peerName={from} />}
            </>
          )}

          {/* Overture decision — replaces Reply entirely */}
          {isOverture && !editing && <OvertureCard letter={letter} mine={mode === 'outbox'} />}

          {/* Addendums (nudges sent with this letter) */}
          {letter.addendums?.length > 0 && (
            <div className="letter-addendums">
              {letter.addendums.map((a, i) => (
                <p key={a.id} className="letter-addendum">
                  <span className="letter-addendum__label">
                    {i === 0 ? 'P.S.' : `P.${String.fromCharCode(83 + i)}.`}
                  </span>{' '}
                  {a.text}
                </p>
              ))}
            </div>
          )}

          {/* "Got your letter" — offered beside Reply, never sent by opening. */}
          {!editing && !replying && mode === 'inbox' && showReply && (
            <AcknowledgeChip letter={letter} peerName={from} />
          )}

          {!editing && (
            <div className="letter-actions">
              {canOpen && (
                <button type="button" className="btn btn--ghost" onClick={() => void onOpen()}>
                  Mark as read
                </button>
              )}
              {showReply && !replying && (
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={!replyEnabled}
                  title={!replyEnabled ? "You've already sent a letter — wait for a reply first" : undefined}
                  onClick={() => replyEnabled && setReplying(true)}
                >
                  Reply
                </button>
              )}
              {canCooldown && (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => { setEditBody(letter.body); setEditing(true); }}
                  >
                    Revise
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={cancelling}
                    onClick={() => void onStop()}
                  >
                    {cancelling ? 'Stopping…' : 'Stop this letter'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Photo attach — sender cooldown, `trusted` pairs only (RT-4) */}
          {!editing && canCooldown && (
            <PhotoAttach letterId={letter.id} relationshipId={letter.relationshipId} />
          )}

          {/* Nudge panel — only for arrived-but-unanswered sent letters */}
          {canNudge && !editing && !replying && (
            <NudgePanel letterId={letter.id} peerName={from} autoOpen={openNudge} />
          )}
        </>
      )}

      {replying && !editing && (
        <form
          className="reply-form reply-form--sheet"
          aria-label={`Reply to @${from}`}
          onSubmit={(e) => { e.preventDefault(); void onSubmitReply(); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setReplying(false);
          }}
        >
          <div className="write-sheet__head">
            <span className="write-sheet__title">Reply to @{from}</span>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setReplying(false)}
              aria-label="Close (your draft is kept)"
              title="Close (your draft is kept)"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <details className="write-sheet__theirs">
            <summary>Their letter</summary>
            <p className="write-sheet__theirs-body">{letter.body}</p>
          </details>
          <ComposerNotes relationshipId={letter.relationshipId} peerName={from} />
          <PostageOfferModal choice={postage} peerName={from} />
          <label htmlFor={`reply-${letter.id}`} className="visually-hidden">Your reply</label>
          <div className={`compose-envelope${postage.enabled || postage.hasDesigns ? ' compose-envelope--stamped' : ''}${sealing ? ' compose-envelope--sealing' : ''}`}>
            <textarea
              id={`reply-${letter.id}`}
              className="form-textarea"
              value={replyBody}
              onChange={(e) => {
                replyPaste.clearNotice();
                onReplyChange(e.target.value);
              }}
              onPaste={replyPaste.onPaste}
              onDrop={replyPaste.onDrop}
              placeholder="A letter back, at the same speed."
              rows={12}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && replyProgress.sendable && !answering) {
                  e.preventDefault();
                  void onSubmitReply();
                }
              }}
            />
            <ComposeStamp
              choice={postage}
              peerName={from}
              sealing={sealing}
              onOpen={showDock ? pointAtDock : undefined}
            />
          </div>
          <ComposeProgress progress={replyProgress} notice={replyPaste.notice} />
          <PaperPreviewButton paper={postage.paper} text={replyBody} />
          {error && (
            <p className="form-error" role="alert">
              <AlertCircle size={13} aria-hidden="true" />{error}
            </p>
          )}
          <div className="form-actions">
            <span className="reply-form__saved">
              <SavedAgo at={draft?.updatedAt} />
            </span>
            <button type="button" className="btn btn--ghost" onClick={() => setReplying(false)}>Cancel</button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={answering || !replyProgress.sendable}
              title={replyProgress.ready ? undefined : `${replyProgress.remaining} characters to go`}
            >
              {answering
                ? 'Sealing…'
                : replyProgress.sendable
                  ? sealLabel('Seal reply', postage)
                  : replyProgress.ready
                    ? 'Keep writing'
                    : `Keep writing · ${replyProgress.remaining} to go`}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}


/**
 * Photo attach (RT-4). Hidden unless the pair currently holds the `photos`
 * capability — the server re-checks on write anyway; this is presentation.
 * A chosen photo waits as a small print with a caption field until the sender
 * attaches it, so the line under it goes up with the picture.
 */
function PhotoAttach({ letterId, relationshipId }: { letterId: string; relationshipId: string }) {
  const { data: relationships } = useMineQuery();
  const [attach, { isLoading }] = useAttachPhotoMutation();
  const [error, setError] = useState<string | null>(null);
  const [encoding, setEncoding] = useState(false);
  const [pending, setPending] = useState<EncodedPhoto | null>(null);
  const [caption, setCaption] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Every hook runs before the capability check below: returning early first
  // changes the hook count once `useMineQuery` resolves, and React throws.
  const onFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setEncoding(true);
    try {
      // Decode + re-encode in the browser (as the avatar editor does), so the
      // label on the file — empty, `image/heic`, or plain wrong — never matters.
      setPending(await fileToJpeg(file));
      setCaption('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not read the photo');
    } finally {
      setEncoding(false);
    }
  }, []);

  const onAttach = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!pending) return;
      setError(null);
      try {
        await attach({ id: letterId, ...pending, caption: caption.trim() || undefined }).unwrap();
        setPending(null);
        setCaption('');
      } catch (err) {
        const er = err as { data?: { message?: string }; message?: string };
        setError(er?.data?.message ?? er?.message ?? 'could not attach the photo');
      }
    },
    [attach, letterId, pending, caption],
  );

  const canPhoto = (relationships ?? []).some(
    (r) => r.id === relationshipId && r.capabilities.includes('photos'),
  );
  if (!canPhoto) return null;
  const busy = encoding || isLoading;

  return (
    <div className="photo-attach">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {pending ? (
        <form className="photo-attach__pending" onSubmit={(e) => void onAttach(e)}>
          <img
            className="photo-attach__thumb"
            src={`data:${pending.mime};base64,${pending.data}`}
            alt=""
            width={pending.width}
            height={pending.height}
          />
          <div className="photo-attach__fields">
            <label htmlFor={`attach-caption-${letterId}`} className="field__label">
              Caption <span className="photo-attach__optional">(optional)</span>
            </label>
            <input
              id={`attach-caption-${letterId}`}
              className="field__input"
              value={caption}
              maxLength={MAX_CAPTION_CHARS}
              placeholder="Write a line under the photo"
              onChange={(e) => setCaption(e.target.value)}
            />
            <div className="form-actions">
              <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setPending(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={busy}>
                {isLoading ? 'Attaching…' : 'Attach photo'}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn btn--ghost"
          style={{ gap: 6, fontSize: '0.875rem' }}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus size={13} aria-hidden="true" />
          {encoding ? 'Reading the photo…' : 'Attach photo'}
        </button>
      )}
      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" /> {error}
        </p>
      )}
    </div>
  );
}

function NudgePanel({
  letterId,
  peerName,
  autoOpen = false,
}: {
  letterId: string;
  peerName: string;
  autoOpen?: boolean;
}) {
  const [nudge, { isLoading }] = useNudgeMutation();
  const { nudgeMaxChars: NUDGE_LIMIT } = useAppSettings();
  const [open, setOpen] = useState(autoOpen);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const onSubmit = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > NUDGE_LIMIT) return;
    setError(null);
    try {
      await nudge({ id: letterId, text: trimmed }).unwrap();
      setText('');
      setSent(true);
      setTimeout(() => { setSent(false); setOpen(false); }, 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not send nudge');
    }
  }, [nudge, letterId, text, NUDGE_LIMIT]);

  if (!open) {
    return (
      <div className="nudge-panel nudge-panel--collapsed">
        <button
          type="button"
          className="btn btn--ghost"
          style={{ gap: 6, fontSize: '0.875rem' }}
          onClick={() => setOpen(true)}
        >
          <Bell size={13} aria-hidden="true" />
          Nudge @{peerName}
        </button>
        <p className="nudge-panel__hint">
          Send a short phrase as a postscript to your letter.
        </p>
      </div>
    );
  }

  return (
    <div className="nudge-panel">
      <div className="nudge-panel__header">
        <span>
          <Bell size={13} aria-hidden="true" style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Nudge @{peerName}
        </span>
        <button type="button" className="icon-btn" style={{ width: 24, height: 24 }} onClick={() => setOpen(false)} aria-label="Cancel">
          <XCircle size={13} />
        </button>
      </div>
      <p className="nudge-panel__desc">
        A short phrase added as a P.S. to this letter — not a new letter.
      </p>
      <div style={{ position: 'relative' }}>
        <label htmlFor={`nudge-${letterId}`} className="visually-hidden">Nudge text</label>
        <textarea
          id={`nudge-${letterId}`}
          ref={inputRef}
          className="form-textarea"
          style={{ minHeight: 52, maxHeight: 80, fontSize: '0.875rem' }}
          value={text}
          maxLength={NUDGE_LIMIT + 10}
          onChange={(e) => setText(e.target.value)}
          placeholder="Just checking in…"
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim().length > 0) {
              e.preventDefault();
              void onSubmit();
            }
          }}
        />
        <span
          className="nudge-panel__count"
          style={{ color: text.length > NUDGE_LIMIT ? 'var(--c-cancelled-text)' : 'var(--c-text3)' }}
        >
          {text.length}/{NUDGE_LIMIT}
        </span>
      </div>
      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={13} aria-hidden="true" /> {error}
        </p>
      )}
      <div className="nudge-panel__actions">
        <span className="nudge-panel__hint">⌘↵ to send</span>
        <button
          type="button"
          className="btn btn--primary"
          style={{ fontSize: '0.8125rem', padding: '6px 14px', borderRadius: 'var(--r-full)', gap: 5 }}
          disabled={isLoading || text.trim().length === 0 || text.length > NUDGE_LIMIT || sent}
          onClick={() => void onSubmit()}
        >
          <Bell size={12} aria-hidden="true" />
          {sent ? 'Nudge sent.' : isLoading ? 'Sending…' : 'Send nudge'}
        </button>
      </div>
    </div>
  );
}

